import { createContext, useContext } from 'react';
import type { ComponentType, ElementType, ReactNode } from 'react';

import type { ApiClient, TaskListItem } from '@zercode/api-client';

// ==============================|| TASKS — WHAT THE HOST APP SUPPLIES ||============================== //
//
// The task drawer is shared by the admin console and the teacher app (TCH-TASKS). Everything that differs
// between them comes in through this one context, so the drawer itself never imports an app:
//
//   client        the app's Operations client (bearer + tenant headers)
//   useMyUserId   the reader — "assign to me", "may I delete this attachment" (a hook, so the provider can sit at
//                 the app root without fetching /me on a page that never opens a task)
//   recordRoute   plan §7.3's deep-link registry — the SAME record lives at different paths per app
//                 (an appointment is /education/appointments/:id for an admin, /appointments/:id for a teacher)
//   linkComponent the router's Link, so "Open record" navigates inside the app instead of reloading it
//   UserPicker    optional: an app with no user directory (teacher) shows the assignee read-only and offers
//                 no watcher picker — the server would refuse a teacher's directory search anyway
//   fetchStatuses the Definitions group `task_status`, in pipeline order
//   notify        the app's toast

/** One task status (Definitions group `task_status`). */
export interface TaskStatusOption {
  id: string;
  code: string;
  name: string;
  color?: string | null;
  isDefault: boolean;
}

export interface TaskUserPickerProps {
  ariaLabel: string;
  value: string;
  extraNames?: Map<string, string>;
  disabled?: boolean;
  onSelectName: (name: string) => void;
  onChange: (id: string) => void;
}

export interface TasksEnvironment {
  client: ApiClient;
  useMyUserId: () => string | null;
  recordRoute: (task: Pick<TaskListItem, 'sourceService' | 'entityType' | 'entityRef'>) => string | null;
  linkComponent: ElementType;
  UserPicker?: ComponentType<TaskUserPickerProps>;
  fetchStatuses: () => Promise<TaskStatusOption[]>;
  notify: (message: string, variant: 'success' | 'error') => void;
}

const TasksEnvironmentContext = createContext<TasksEnvironment | null>(null);

export function TasksEnvironmentProvider({ value, children }: { value: TasksEnvironment; children: ReactNode }) {
  return <TasksEnvironmentContext.Provider value={value}>{children}</TasksEnvironmentContext.Provider>;
}

export function useTasksEnvironment(): TasksEnvironment {
  const env = useContext(TasksEnvironmentContext);
  if (!env) throw new Error('useTasksEnvironment: wrap the task UI in <TasksEnvironmentProvider>.');
  return env;
}

/**
 * Plan §7.3 for a whole app: `(sourceService:entityType) → path`. An unknown pair has no route and renders as
 * plain text — the DB stores no URLs, because the owner of a record is its service and routes change.
 */
export function recordRouteFrom(routes: Record<string, (id: string) => string>): TasksEnvironment['recordRoute'] {
  return (task) => {
    if (!task.sourceService || !task.entityType || !task.entityRef) return null;
    const route = routes[`${task.sourceService}:${task.entityType}`];
    return route ? route(task.entityRef) : null;
  };
}
